import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../../../../environments/environment';

export type LoadingType = 'default' | 'download' | 'upload' | 'process';

@Injectable({
  providedIn: 'root'
})
export class LoadingService {
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private loadingTypeSubject = new BehaviorSubject<LoadingType>('default');
  private showProgressSubject = new BehaviorSubject<boolean>(false);
  private loadingProgressSubject = new BehaviorSubject<number>(0);
  private loadingMessageSubject = new BehaviorSubject<string>('');

  constructor() {}

  // Observable streams
  get loading$(): Observable<boolean> {
    return this.loadingSubject.asObservable();
  }

  get loadingType$(): Observable<LoadingType> {
    return this.loadingTypeSubject.asObservable();
  }

  get showProgress$(): Observable<boolean> {
    return this.showProgressSubject.asObservable();
  }

  get loadingProgress$(): Observable<number> {
    return this.loadingProgressSubject.asObservable();
  }

  get loadingMessage$(): Observable<string> {
    return this.loadingMessageSubject.asObservable();
  }

  // Current values
  get loading(): boolean {
    return this.loadingSubject.value;
  }

  get loadingType(): LoadingType {
    return this.loadingTypeSubject.value;
  }

  get showProgress(): boolean {
    return this.showProgressSubject.value;
  }

  get loadingProgress(): number {
    return this.loadingProgressSubject.value;
  }

  get loadingMessage(): string {
    return this.loadingMessageSubject.value;
  }

  // Methods to update loading state
  startLoading(type: LoadingType = 'default', showProgress: boolean = false, message?: string): void {
    this.loadingSubject.next(true);
    this.loadingTypeSubject.next(type);
    this.showProgressSubject.next(showProgress);
    this.loadingProgressSubject.next(0);
    
    if (message) {
      this.loadingMessageSubject.next(message);
    } else {
      // Use default messages from environment based on type
      switch (type) {
        case 'default':
          this.loadingMessageSubject.next(environment.GLOBAL_SETTINGS.MESSAGE_LOADING_INFORMATION);
          break;
        case 'process':
          this.loadingMessageSubject.next(environment.GLOBAL_SETTINGS.MESSAGE_PROCESSING_INFORMATION);
          break;
        case 'download':
          this.loadingMessageSubject.next(environment.GLOBAL_SETTINGS.GENERATING_DOCUMENT);
          break;
        case 'upload':
          this.loadingMessageSubject.next(environment.GLOBAL_SETTINGS.MESSAGE_PROCESSING_INFORMATION);
          break;
      }
    }
  }

  stopLoading(): void {
    this.loadingSubject.next(false);
    this.resetProgress();
  }

  updateProgress(progress: number, message?: string): void {
    this.loadingProgressSubject.next(progress);
    if (message) {
      this.loadingMessageSubject.next(message);
    }
  }

  updateMessage(message: string): void {
    this.loadingMessageSubject.next(message);
  }

  resetProgress(): void {
    this.loadingProgressSubject.next(0);
    this.showProgressSubject.next(false);
  }

  // Helper method to simulate progress
  simulateProgress(targetProgress: number = 90, steps: number = 10, interval: number = 300, messages?: { [key: number]: string }): void {
    if (!this.showProgress) {
      this.showProgressSubject.next(true);
    }

    const stepSize = targetProgress / steps;
    let currentStep = 0;

    const progressInterval = setInterval(() => {
      if (this.loadingProgress < targetProgress) {
        currentStep++;
        const newProgress = Math.min(stepSize * currentStep, targetProgress);
        this.loadingProgressSubject.next(newProgress);
        
        // Update message if provided for this progress level
        if (messages && messages[newProgress]) {
          this.loadingMessageSubject.next(messages[newProgress]);
        }
      } else {
        clearInterval(progressInterval);
      }
    }, interval);
  }

  completeProgress(finalMessage?: string): void {
    this.loadingProgressSubject.next(100);
    if (finalMessage) {
      this.loadingMessageSubject.next(finalMessage);
    }
  }
} 